import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Heart, Users, BookOpen, Lock, ArrowRight, CheckCircle, Sparkles, MessageCircle, Brain, AlertTriangle, Zap, Star } from 'lucide-react';
export function LandingPage() {
  const navigate = useNavigate();
  const features = [{
    icon: BookOpen,
    title: 'Beyond Biology',
    description: 'Periods, puberty, emotions, relationships—the full picture, not just anatomy',
    gradient: 'from-purple-500 to-pink-500'
  }, {
    icon: Shield,
    title: 'Violence Prevention',
    description: 'Real talk about consent, boundaries, and preventing sexual violence—backed by facts',
    gradient: 'from-blue-500 to-cyan-500'
  }, {
    icon: Heart,
    title: 'Mental Wellness',
    description: 'Self-worth, body image, peer pressure, and navigating social expectations',
    gradient: 'from-rose-500 to-orange-500'
  }, {
    icon: Users,
    title: 'Real-Life Scenarios',
    description: 'Interactive stories that adapt to your gender and teach life-saving skills',
    gradient: 'from-teal-500 to-green-500'
  }, {
    icon: MessageCircle,
    title: 'Ask Anything',
    description: 'Anonymous Q&A—no question is too awkward or too personal',
    gradient: 'from-indigo-500 to-purple-500'
  }, {
    icon: Brain,
    title: 'Age-Adaptive Learning',
    description: 'Content that grows with you, from 13 to adulthood',
    gradient: 'from-cyan-500 to-blue-500'
  }];
  const stats = [{
    number: '100%',
    label: 'Anonymous & Private',
    icon: Lock
  }, {
    number: '24/7',
    label: 'Access Anytime',
    icon: Zap
  }, {
    number: 'Safe',
    label: 'India Compliant',
    icon: Shield
  }];
  return <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 dark:from-slate-950 dark:via-purple-950 dark:to-slate-900" data-id="element-467">
      {/* Navigation */}
      <nav className="glass-effect glass-effect-dark sticky top-0 z-50 border-b border-purple-100 dark:border-purple-900/30" data-id="element-468">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-469">
          <div className="flex items-center justify-between h-16" data-id="element-470">
            <div className="flex items-center space-x-3" data-id="element-471">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg" data-id="element-472">
                <Shield className="w-6 h-6 text-white" data-id="element-473" />
              </div>
              <span className="text-xl font-bold gradient-text from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400" data-id="element-474">
                ItMatters
              </span>
            </div>
            <button onClick={() => navigate('/welcome')} className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300" data-id="element-475">
              Get Started
            </button>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="relative overflow-hidden pattern-dots" data-id="element-476">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5" data-id="element-477" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 relative" data-id="element-478">
          <div className="text-center max-w-4xl mx-auto animate-fade-in" data-id="element-479">
            <div className="inline-flex items-center space-x-2 glass-effect glass-effect-dark rounded-full px-4 py-2 mb-6 border border-purple-200 dark:border-purple-800" data-id="element-480">
              <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" data-id="element-481" />
              <span className="text-sm font-medium text-purple-900 dark:text-purple-300" data-id="element-482">
                Made for India's Youth
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight" data-id="element-483">
              Because
              <br data-id="element-484" />
              <span className="gradient-text from-purple-600 via-pink-600 to-orange-500 animate-gradient" data-id="element-485">
                It Matters
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed" data-id="element-486">
              Most sexual violence happens in silence—because honest
              conversations never start.
              <br data-id="element-487" />
              <span className="font-semibold text-purple-900 dark:text-purple-300" data-id="element-488">
                Break the silence. Learn the truth. Drive change.
              </span>
            </p>
            <button onClick={() => navigate('/welcome')} className="group inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-10 py-5 rounded-2xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300" data-id="element-489">
              <span data-id="element-490">Start Your Journey</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-491" />
            </button>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-4" data-id="element-492">
              No sign-up required • Completely anonymous • Takes 2 minutes
            </p>
          </div>
          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto mt-16" data-id="element-493">
            {stats.map((stat, index) => {
            const Icon = stat.icon;
            return <div key={index} className="glass-effect glass-effect-dark rounded-2xl p-6 text-center card-hover border border-purple-100 dark:border-purple-900/30" data-id="element-494">
                  <Icon className="w-8 h-8 mx-auto mb-2 text-purple-600 dark:text-purple-400" data-id="element-495" />
                  <p className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-496">
                    {stat.number}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-497">
                    {stat.label}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>
      {/* Critical Statistics Section */}
      <section className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-y border-red-100 dark:border-red-900/30 py-20" data-id="element-498">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-499">
          <div className="max-w-4xl mx-auto" data-id="element-500">
            <div className="flex items-center justify-center space-x-3 mb-8" data-id="element-501">
              <AlertTriangle className="w-10 h-10 text-red-500" data-id="element-502" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white text-center" data-id="element-503">
                The Reality We Must Face
              </h2>
            </div>
            <div className="glass-effect glass-effect-dark border border-red-200 dark:border-red-900/30 rounded-3xl p-8 mb-8" data-id="element-504">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-505">
                <div className="text-center" data-id="element-506">
                  <p className="text-4xl font-bold text-red-600 dark:text-red-400 mb-2" data-id="element-507">
                    31,000+
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-508">
                    Rape cases reported annually in India (NCRB 2022)
                  </p>
                </div>
                <div className="text-center" data-id="element-509">
                  <p className="text-4xl font-bold text-red-600 dark:text-red-400 mb-2" data-id="element-510">
                    53%
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-511">
                    Of children face some form of sexual abuse (MWCD Study)
                  </p>
                </div>
                <div className="text-center" data-id="element-512">
                  <p className="text-4xl font-bold text-red-600 dark:text-red-400 mb-2" data-id="element-513">
                    90%+
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-514">
                    Cases go unreported due to shame and stigma
                  </p>
                </div>
              </div>
              <p className="text-lg text-gray-800 dark:text-gray-200 leading-relaxed mb-4" data-id="element-515">
                <span className="font-bold" data-id="element-516">Here's the truth:</span> Most sexual
                violence happens because we never learned to talk about consent,
                boundaries, and respect. Because sex education was reduced to a
                biology chapter. Because asking questions was considered
                shameful.
              </p>
              <p className="text-xl font-semibold text-gray-900 dark:text-white" data-id="element-517">
                Silence doesn't protect anyone. Knowledge does.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-20" data-id="element-518">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-519">
          <div className="text-center mb-16" data-id="element-520">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-521">
              Everything You Actually Need to Know
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto" data-id="element-522">
              Sex education isn't just about reproduction. It's about
              understanding yourself, your relationships, and your choices.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-523">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <div key={index} className="group glass-effect glass-effect-dark border border-purple-100 dark:border-purple-900/30 rounded-3xl p-6 card-hover" data-id="element-524">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`} data-id="element-525">
                    <Icon className="w-7 h-7 text-white" data-id="element-526" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-527">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-528">
                    {feature.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20" data-id="element-529">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-530">
          <div className="max-w-3xl mx-auto text-center" data-id="element-531">
            <Star className="w-16 h-16 mx-auto mb-6 text-yellow-500" data-id="element-532" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-533">
              Ready to Start Learning?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8" data-id="element-534">
              Most textbooks tell you how bodies work. We help you understand
              your story. Talking about sex, relationships, consent, and
              emotions shouldn't be taboo—it's how you grow.
            </p>
            <button onClick={() => navigate('/welcome')} className="group inline-flex items-center space-x-3 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white px-12 py-6 rounded-2xl font-semibold text-xl hover:shadow-2xl hover:scale-105 transition-all duration-300" data-id="element-535">
              <span data-id="element-536">Start by Telling Us Your Age</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-537" />
            </button>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-4" data-id="element-538">
              Your journey is private, safe, and just for you
            </p>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="glass-effect glass-effect-dark border-t border-purple-100 dark:border-purple-900/30 py-12" data-id="element-539">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-540">
          <div className="text-center" data-id="element-541">
            <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-542">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg" data-id="element-543">
                <Shield className="w-6 h-6 text-white" data-id="element-544" />
              </div>
              <span className="text-2xl font-bold gradient-text from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400" data-id="element-545">
                ItMatters
              </span>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto" data-id="element-546">
              Made for India's youth. Localized resources. Real support when you
              need it. Anonymous, secure, and judgment-free.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-547">
              <span className="flex items-center space-x-2" data-id="element-548">
                <Lock className="w-4 h-4" data-id="element-549" />
                <span data-id="element-550">100% Anonymous</span>
              </span>
              <span className="text-purple-400" data-id="element-551">•</span>
              <span className="flex items-center space-x-2" data-id="element-552">
                <Shield className="w-4 h-4" data-id="element-553" />
                <span data-id="element-554">Secure Platform</span>
              </span>
              <span className="text-purple-400" data-id="element-555">•</span>
              <span className="flex items-center space-x-2" data-id="element-556">
                <Heart className="w-4 h-4" data-id="element-557" />
                <span data-id="element-558">Judgment-Free</span>
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-6" data-id="element-559">
              © 2024 ItMatters. Supporting India's youth with honest,
              accessible education.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}